import { useTranslation } from "react-i18next";
import { CalendarBlank, Check, Flag, Prohibit } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { TNextStep } from "@/zapi-frontend/stores/project";
import { fillFigureText, refsAt, type TFigureLookup } from "@/zapi-frontend/shared/lib/figureRefs";
import { useFigureRefs } from "../../../MemoIsland/FigureRefs/FigureRefsProvider";
import { FigureText } from "../../../MemoIsland/FigureRefs/FigureTrigger";
import { HomeWidget } from "./HomeWidget";

function parseStep(step: TNextStep) {
  const parts = step.text.split("|").map((p) => p.trim());
  const action = step.summary || parts[0] || step.text;
  const timeframe = parts.find((p) => /day|week|month|quarter|next/i.test(p) && p.length < 40);
  const criteria = parts[parts.length - 1] || "";
  const killIdx = criteria.search(/kill\s*:/i);
  const success = killIdx >= 0 ? criteria.slice(0, killIdx) : criteria;
  const kill = killIdx >= 0 ? criteria.slice(killIdx) : "";
  const clean = (s: string) => s.replace(/^(success|gate|kill)\s*:/i, "").trim();
  return { action, timeframe, success: clean(success), kill: clean(kill) };
}

type TStepField = "text" | "summary" | "timeframe" | "successCriteria" | "killCriteria";

export function describeNextStep(step: TNextStep, figures?: TFigureLookup | null) {
  const fill = (field: TStepField) => fillFigureText(step[field] ?? "", refsAt(step, field), figures).text;
  const text = fill("text");
  const hasStructured = !!(step.timeframe || step.successCriteria || step.killCriteria);
  const legacy = hasStructured ? null : parseStep({ ...step, text });
  return {
    text,
    action: fill("summary") || legacy?.action || text,
    timeframe: fill("timeframe") || legacy?.timeframe || "",
    success: fill("successCriteria") || legacy?.success || "",
    kill: fill("killCriteria") || legacy?.kill || ""
  };
}

function StepCheck({ checked }: { checked: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-[4px] border shadow-xs",
        checked ? "border-primary bg-primary text-primary-foreground" : "border-input bg-card"
      )}
    >
      {checked && <Check className="size-3" weight="bold" />}
    </span>
  );
}

function StepRow({ step }: { step: TNextStep }) {
  const figures = useFigureRefs();
  const described = describeNextStep(step, figures);
  const isPipedText = described.text.includes("|");
  const showsFields = isPipedText || !!(step.timeframe || step.successCriteria || step.killCriteria);
  const field = (name: "timeframe" | "successCriteria" | "killCriteria", fallback: string) => {
    const value = step[name];
    return value ? <FigureText text={value} refs={refsAt(step, name)} /> : fallback;
  };
  const action = step.summary ? <FigureText text={step.summary} refs={refsAt(step, "summary")} /> : described.action;
  const timeframe = showsFields ? field("timeframe", described.timeframe) : "";
  const success = showsFields ? field("successCriteria", described.success) : "";
  const kill = showsFields ? field("killCriteria", described.kill) : "";
  const detail = step.summary && !isPipedText && described.text !== described.action ? described.text : "";

  return (
    <li className={cn("flex gap-3 px-5 py-4", step.completed && "opacity-60")}>
      <StepCheck checked={step.completed} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span className={cn("typo-label-md text-foreground", step.completed && "line-through")}>{action}</span>
        {detail && <p className="typo-body-sm text-muted-foreground">{detail}</p>}
        {(timeframe || success || kill) && (
          <div className="typo-caption flex flex-wrap gap-x-4 gap-y-1 pt-0.5 text-muted-foreground">
            {timeframe && (
              <span className="flex items-center gap-1">
                <CalendarBlank className="size-3.5" aria-hidden />
                {timeframe}
              </span>
            )}
            {success && (
              <span className="flex items-center gap-1">
                <Flag className="size-3.5 text-success" aria-hidden />
                {success}
              </span>
            )}
            {kill && (
              <span className="flex items-center gap-1">
                <Prohibit className="size-3.5 text-destructive" aria-hidden />
                {kill}
              </span>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

export function NextStepsBlock({ nextSteps }: { nextSteps: TNextStep[] }) {
  const { t } = useTranslation("projectHome");
  if (!nextSteps.length) return null;
  return (
    <HomeWidget title={t("overview.nextSteps")} bodyClassName="p-0">
      <ul className="divide-y">
        {nextSteps.map((step) => (
          <StepRow key={step.id} step={step} />
        ))}
      </ul>
    </HomeWidget>
  );
}
